import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  withField,
  ArrayField,
  Form,
  Dropdown,
  Toast,
  Typography,
  Tooltip,
  Tag,
  Modal,
} from "@douyinfe/semi-ui";
import { getLocalStorage, setLocalStorage } from "@utils/localstorage";
import { setIsJobSubmited } from "@state/global/reducer";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@state/store";
import { isEqual } from "lodash";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { ReactComponent as DeleteIcon } from "@assets/icons/delete.svg";
import { ReactComponent as MinimizeIcon } from "@assets/icons/minimize.svg";
import { ReactComponent as MaximizeIcon } from "@assets/icons/maximize.svg";
import { ReactComponent as AtIcon } from "@assets/icons/at_icon.svg";
import { ReactComponent as ArrowLeftIcon } from "@assets/icons/arrow_left_icon.svg";
import { IconPlus, IconSearch, IconMore } from "@douyinfe/semi-icons";
import { ReactComponent as BulbIcon } from "@assets/icons/bulb_icon.svg";
import {
  antibodyChainValidate,
  binderSequenceValidate,
} from "@utils/validator";
import { getUserInfoApi } from "@services/user/user.api";
import { ADVANCED_PROTEIN_BINDER_DESIGN_TYPES } from "@services/home/config/home.config";
import {
  BinderDesignTypeEnum,
  VhhDesignTypeEnum,
} from "@services/home/enum/home.enum";
import { debounce } from "lodash";
import type {
  BaseFormApi as FormApi,
  FormState,
} from "@douyinfe/semi-foundation/lib/es/form/interface";
import {
  TokenInfoModel,
  JobModel,
  JobSubmitInfoModel,
  BinderSequenceModel,
  AdvancedProteinBinderAttachmentModel,
  ProteinBinderDesignPayloadModel,
  ProvideStructureDataModel,
  GeoFlowLiteModel,
} from "@services/home/model/home.model";
import classNames from "classnames";
import { replaceWhitespace } from "@utils/common.util";
import {
  getProteinBinderDesignTokenInfoApi,
  createProteinBinderDesignApi,
} from "@services/home/home.api";
import {
  getProvideResidueMap,
  getHotspotResidueMap,
  getBinderPlaceholder,
} from "@utils/data-process.util";
import JobLoginDialog from "../job-login-dialog";
import HotspotEditor from "../hotspot-editor";
import ProvideStructureDialog from "../provide-structure-dialog";
import styles from "./index.module.scss";

const { TextArea, InputNumber } = Form;

const { Text } = Typography;

interface Prop {
  value?: AdvancedProteinBinderAttachmentModel;
  exampleJobs: JobModel[];
  onChange?: (values: any) => void;
  switchSimpleMode: () => void;
}
const ProteinBinderDesignTableField: React.FC<Prop> = ({
  value,
  exampleJobs,
  onChange,
  switchSimpleMode,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const formRef = useRef<FormApi<AdvancedProteinBinderAttachmentModel>>();
  const navigate = useNavigate();

  const inputRefs = useRef<Record<string, HTMLTextAreaElement>>({});
  const [tokenInfo, setTokenInfo] = useState<TokenInfoModel>();
  const initSequencesData: BinderSequenceModel[] = [
    {
      entity_type: BinderDesignTypeEnum.ProteinChain,
      copies: 1,
      entity_sequence: "",
      isEdit: true,
    },
  ];

  const [isDeepThink, setIsDeepThink] = useState(false);
  const [loginVisible, setLoginVisible] = useState(false);

  const [loadingInfo, setLoadingInfo] = useState(false);
  const [jobParameter, setJobParameter] =
    useState<ProteinBinderDesignPayloadModel>();

  const [userType, setUserType] = useState("");

  const [username, setUsername] = useState<string>();

  const [loading, setLoading] = useState(false);

  const [submitingJob, setSubmitingJob] = useState(false);

  const [provideDialogVisible, setProvideDialogVisible] = useState(false);

  const [editedRow, setEditedRow] = useState<BinderSequenceModel>();

  const [editedIndex, setEditedIndex] = useState<number>();

  function onSeqBlur(field: string) {
    if (!formRef.current) {
      return;
    }

    const target = formRef.current.getValue(
      field as any
    ) as BinderSequenceModel;
    const hasError = antibodyChainValidate(target.entity_sequence, true);
    if (!hasError) {
      formRef.current.setValue(`${field}.isEdit` as any, false);
    }
  }

  function onEditSeq(key: string, field: string) {
    if (!formRef.current) {
      return;
    }
    const target = formRef.current.getValue(
      field as any
    ) as BinderSequenceModel;
    if (!target.isEdit) {
      formRef.current.setValue(`${field}.isEdit` as any, true);
    }

    setTimeout(() => {
      const textDom = inputRefs.current[key];
      const val = textDom.value;
      textDom.focus();
      textDom.setSelectionRange(val.length, val.length);
    }, 0);
  }

  function setValues(
    p: AdvancedProteinBinderAttachmentModel,
    deepthink: boolean
  ) {
    if (onChange) {
      onChange({
        ...p,
        deepthink,
      });
    }
  }

  function getTokenInfo(p: ProteinBinderDesignPayloadModel) {
    setLoadingInfo(true);

    getProteinBinderDesignTokenInfoApi(p)
      .then((data) => {
        setTokenInfo(data);
        setLoadingInfo(false);
      })
      .catch(() => {
        setTokenInfo(undefined);
        setLoadingInfo(false);
      });
  }

  function onLogined() {
    if (!jobParameter) {
      return;
    }
    getTokenInfo(jobParameter);
  }

  async function submitJob() {
    if (!formRef.current) {
      return;
    }

    try {
      const values = await formRef.current.validate();

      const params: ProteinBinderDesignPayloadModel = {
        job_name: "",
        geo_flow_lite: {
          mode: "multi_entity",
          deepthink: isDeepThink,
          entity_list: values.entity_list,
        },
      };

      const targetJob = exampleJobs.find((item) => {
        const parameters = item.parameters as ProteinBinderDesignPayloadModel;

        if (parameters.geo_flow_lite) {
          const targetGeoflow: GeoFlowLiteModel = {
            mode: parameters.geo_flow_lite.mode,
            deepthink: parameters.geo_flow_lite.deepthink,
            entity_list: parameters.geo_flow_lite?.entity_list,
          };
          const isSame = isEqual(params.geo_flow_lite, targetGeoflow);

          return isSame;
        } else {
          return false;
        }
      });

      if (targetJob) {
        navigate(`/job-detail/${targetJob.id}`);
        const jobSubmitedInfo =
          getLocalStorage<JobSubmitInfoModel>("is_job_submited");
        if (!jobSubmitedInfo) {
          dispatch(setIsJobSubmited(true));
          setLocalStorage("is_job_submited", {
            submited: true,
          } as JobSubmitInfoModel);
        }
        return;
      }

      setJobParameter(params);

      setLoading(true);

      const { code, data } = await getUserInfoApi();

      if (code === 200) {
        setUserType("registered");
        setUsername(data.username);
        getTokenInfo(params);
      } else {
        setUserType("anonymous");
        setUsername(undefined);
      }
      setLoading(false);
      setLoginVisible(true);
    } catch (e) {
      if (e instanceof AxiosError) {
        setUserType("anonymous");
        setUsername(undefined);
        setLoginVisible(true);
      }

      setLoading(false);
    }
  }

  function onSubmit() {
    submitJob();
  }

  function onToggleDeepThink() {
    setIsDeepThink(!isDeepThink);
  }

  function onValueChange(values: AdvancedProteinBinderAttachmentModel) {
    console.log("onValueChange-->", values);
  }

  const debouncedOnValueChange = useMemo(
    () => debounce(onValueChange, 500),
    [onValueChange]
  );

  function changeEntityList(
    cb: (input: BinderSequenceModel[]) => BinderSequenceModel[]
  ) {
    if (!formRef.current) {
      return;
    }

    const sequences = formRef.current.getValue("entity_list");
    const output = cb(sequences);

    formRef.current.setValue("entity_list", output);
  }

  function onDeleteRow(idx: number, key: string) {
    changeEntityList((sequences) => {
      const list = sequences.filter((item, i) => {
        return i !== idx;
      });

      return list;
    });

    delete inputRefs.current[key];
  }

  function onCollapseToggle(idx: number) {
    changeEntityList((sequences) => {
      const list = sequences.map((item, i) => {
        const o = { ...item };

        if (idx === i) {
          o.isCollapse = !o.isCollapse;
        }

        return o;
      });

      return list;
    });
  }

  function onProvideSubmit(data: ProvideStructureDataModel, fid: string) {
    const idx = editedIndex;
    if (typeof idx !== "number") {
      return;
    }

    changeEntityList((sequences) => {
      const list = sequences.map((item, i) => {
        const o = { ...item };
        if (i === idx) {
          o.entity_sequence = data.entity_sequence || item.entity_sequence;
          o.coord_items = data.coord_items;
          o.pdb_input_type = data.input_type;
          o.pdb_input_value = data.input_value;
          o.coord_struct_id = fid;
          o.isEdit = false;
        }

        return o;
      });

      return list;
    });

    formRef.current?.setError(`entity_list[${idx}].entity_sequence` as any, "");

    setProvideDialogVisible(false);
  }

  function onProvideReset(seq: string) {
    const idx = editedIndex;
    if (typeof idx !== "number") {
      return;
    }

    changeEntityList((sequences) => {
      const list = sequences.map((item, i) => {
        const o = { ...item };
        if (i === idx) {
          o.coord_items = undefined;
          o.coord_struct_id = undefined;
          o.pdb_input_type = undefined;
          o.pdb_input_value = undefined;
          o.entity_sequence = seq;
        }

        return o;
      });

      return list;
    });

    setProvideDialogVisible(false);
  }

  function onLoginCancel() {
    setLoginVisible(false);
  }

  function onLoginSubmit(job_name?: string) {
    if (!jobParameter || !job_name) {
      return;
    }

    const params = {
      ...jobParameter,
      job_name,
    };

    setSubmitingJob(true);
    createProteinBinderDesignApi(params)
      .then(({ code, data, message }) => {
        if (code === 200) {
          Toast.success(message);
          navigate(`/job-detail/${data.job_id}`);
          const jobSubmitedInfo =
            getLocalStorage<JobSubmitInfoModel>("is_job_submited");
          if (!jobSubmitedInfo) {
            dispatch(setIsJobSubmited(true));
            setLocalStorage("is_job_submited", {
              submited: true,
            } as JobSubmitInfoModel);
          }
          onLoginCancel();
        }

        setSubmitingJob(false);
      })
      .catch((e) => {
        setSubmitingJob(false);
        console.error(e);
      });
  }

  function onEditHotspot(idx: number) {
    changeEntityList((sequences) => {
      const list = sequences.map((item, i) => {
        const o = { ...item };

        if (idx === i) {
          o.hotspotShow = true;
        }

        return o;
      });

      return list;
    });
  }

  function onHotspotCancel(idx: number) {
    changeEntityList((sequences) => {
      const list = sequences.map((item, i) => {
        const o = { ...item };

        if (idx === i) {
          o.hotspotShow = false;
        }

        return o;
      });

      return list;
    });
  }

  function onHotspotSubmit(v: string, idx: number) {
    changeEntityList((sequences) => {
      const list = sequences.map((item, i) => {
        const o = { ...item };

        if (idx === i) {
          o.hotspots = v ? v : undefined;
          o.hotspotShow = false;
        }

        return o;
      });

      return list;
    });
  }

  function onDeleteCorrdItem(idx: number, k: number) {
    changeEntityList((sequences) => {
      const list = sequences.map((item, i) => {
        const o = { ...item };

        if (idx === i) {
          o.coord_items = (o.coord_items || []).filter((_, j) => {
            return k !== j;
          });
        }

        return o;
      });

      return list;
    });
  }

  function onModeChange() {
    Modal.warning({
      title: "Warning",
      content:
        "Are you sure you want to switch to simple mode? Your current inputs will be lost.",
      cancelText: "Cancel",
      okText: "Ok",
      onOk: () => {
        switchSimpleMode();
      },
    });
  }

  function onOpenProvideDialog(idx: number, row: BinderSequenceModel) {
    setProvideDialogVisible(true);
    setEditedIndex(idx);
    setEditedRow(row);
  }

  function onCancelProvide() {
    setProvideDialogVisible(false);
  }

  function formatText(item: BinderSequenceModel) {
    if (item.pdb_input_type === VhhDesignTypeEnum.PdbID) {
      return `Ref: PDB ${item.pdb_input_value || ""}`;
    } else if (item.pdb_input_type === VhhDesignTypeEnum.UniProtID) {
      return `Ref: UniProt ${item.pdb_input_value || ""}`;
    } else if (item.pdb_input_type === VhhDesignTypeEnum.AntigenSequence) {
      return `Ref: ${item.pdb_input_value || ""}`;
    } else if (item.pdb_input_type === VhhDesignTypeEnum.UploadStructure) {
      return `Ref: ${item.pdb_input_value || ""}`;
    } else {
      return "-";
    }
  }

  useEffect(() => {
    if (value) {
      setIsDeepThink(value.deepthink);

      formRef.current?.setValue("entity_list", []);

      setTimeout(() => {
        formRef.current?.setValue("entity_list", value.entity_list);
      }, 20);
    }
  }, [JSON.stringify(value)]);

  return (
    <>
      <Form
        className={styles.container}
        getFormApi={(formApi) => (formRef.current = formApi)}
        // onValueChange={debouncedOnValueChange}
      >
        {({
          formState,
        }: {
          formState: FormState<AdvancedProteinBinderAttachmentModel>;
        }) => {
          return (
            <ArrayField field="entity_list" initValue={initSequencesData}>
              {({ arrayFields, addWithInitValue }) => (
                <>
                  <div className="sequence-input-container appear-animate">
                    <div className="sequence-header">
                      <div className="num-column">Entity ID</div>
                      <div className="select-column">Entity type</div>
                      <div className="name-column">Copies</div>
                      <div className="sequence-column">Sequence</div>
                      <div className="operate-column"></div>
                    </div>
                    <div className="sequence-body">
                      {arrayFields.map(({ field, key, remove }, i) => {
                        const sequences = formState.values?.entity_list;

                        const map = getProvideResidueMap(
                          sequences?.[i]?.coord_items
                        );

                        const hotsportMap = getHotspotResidueMap(
                          sequences?.[i]?.hotspots
                        );

                        return (
                          <div
                            className="sequence-content appear-animate"
                            key={key}
                          >
                            {sequences && sequences[i] && (
                              <div className="content-input-row">
                                <div className="num-column">{i + 1}</div>
                                <div className="select-column">
                                  <Form.Select
                                    field={`${field}[entity_type]`}
                                    noLabel
                                    style={{ width: "100%" }}
                                  >
                                    {ADVANCED_PROTEIN_BINDER_DESIGN_TYPES.map(
                                      (item) => {
                                        return (
                                          <Form.Select.Option
                                            value={item.value}
                                            key={item.value}
                                          >
                                            {item.name}
                                          </Form.Select.Option>
                                        );
                                      }
                                    )}
                                  </Form.Select>
                                  {sequences[i].entity_type !==
                                    BinderDesignTypeEnum.Ligand &&
                                    sequences[i].entity_type !==
                                      BinderDesignTypeEnum.LigandCCD && (
                                      <>
                                        {sequences[i].coord_struct_id ? (
                                          <Text
                                            className={styles.provideText}
                                            onClick={() =>
                                              onOpenProvideDialog(
                                                i,
                                                sequences[i]
                                              )
                                            }
                                            style={{ width: 156 }}
                                            ellipsis
                                          >
                                            {formatText(sequences[i])}
                                          </Text>
                                        ) : (
                                          <Text
                                            // link
                                            // underline
                                            className={styles.provideText}
                                            onClick={() =>
                                              onOpenProvideDialog(
                                                i,
                                                sequences[i]
                                              )
                                            }
                                          >
                                            ↑Provide structure
                                          </Text>
                                        )}
                                      </>
                                    )}
                                </div>
                                <div className="name-column">
                                  <InputNumber
                                    innerButtons={true}
                                    field={`${field}[copies]`}
                                    noLabel
                                    min={1}
                                    max={4}
                                    style={{ width: 72 }}
                                  />
                                </div>
                                {sequences[i] && (
                                  <div className="sequence-column">
                                    <div
                                      className={classNames({
                                        [styles.seqWrapper]: true,
                                        [styles.hidden]:
                                          !sequences[i].isCollapse,
                                      })}
                                    >
                                      <Text
                                        ellipsis={true}
                                        style={{
                                          maxWidth: 224,
                                          color: "var(--semi-color-text-2)",
                                        }}
                                      >
                                        {sequences[i].entity_sequence}
                                      </Text>
                                      (length{" "}
                                      {
                                        replaceWhitespace(
                                          sequences[i].entity_sequence
                                        ).length
                                      }
                                      )
                                    </div>
                                    <TextArea
                                      rows={3}
                                      field={`${field}[entity_sequence]`}
                                      noLabel
                                      validate={(v: string) =>
                                        binderSequenceValidate(
                                          v,
                                          sequences[i].entity_type
                                        )
                                      }
                                      trigger="blur"
                                      className={classNames({
                                        [styles.hidden]:
                                          !sequences[i].isEdit ||
                                          sequences[i].isCollapse,
                                      })}
                                      ref={(ref: HTMLTextAreaElement) => {
                                        inputRefs.current[key] = ref;
                                      }}
                                      onBlur={() => onSeqBlur(field)}
                                      placeholder={getBinderPlaceholder(
                                        sequences[i].entity_type
                                      )}
                                    />

                                    <div
                                      className={classNames({
                                        "sequence-container": true,
                                        [styles.hidden]:
                                          sequences[i].isEdit ||
                                          sequences[i].isCollapse,
                                      })}
                                      onPointerDown={() =>
                                        onEditSeq(key, field)
                                      }
                                    >
                                      <div className="seq-wrapper">
                                        {replaceWhitespace(
                                          sequences[i].entity_sequence
                                        )
                                          .split("")
                                          .map((residue, index) => {
                                            return (
                                              <span
                                                className="seq"
                                                key={index}
                                                style={{
                                                  backgroundColor: map[
                                                    index + 1
                                                  ]
                                                    ? "rgb(var(--semi-brand2-0))"
                                                    : "",
                                                  color: hotsportMap[index + 1]
                                                    ? "#F01D94"
                                                    : "",
                                                }}
                                              >
                                                {residue}
                                                <span className="label">
                                                  {index + 1}
                                                </span>
                                              </span>
                                            );
                                          })}
                                      </div>
                                    </div>
                                    {!sequences[i].hotspotShow &&
                                      sequences[i].coord_items && (
                                        <div>
                                          {sequences[i].coord_items.map(
                                            (item, k) => {
                                              return (
                                                <Tag
                                                  key={k}
                                                  closable
                                                  className={styles.tag}
                                                  onClose={(v, e) => {
                                                    e.preventDefault();
                                                    onDeleteCorrdItem(i, k);
                                                  }}
                                                >
                                                  {item.struct_segment} → seq{" "}
                                                  {item.seq_segment}
                                                </Tag>
                                              );
                                            }
                                          )}
                                        </div>
                                      )}

                                    {sequences[i].hotspotShow && (
                                      <HotspotEditor
                                        sequence={sequences[i]}
                                        cancel={() => onHotspotCancel(i)}
                                        submit={(v: string) =>
                                          onHotspotSubmit(v, i)
                                        }
                                      />
                                    )}
                                  </div>
                                )}

                                <div className="operate-column">
                                  <Dropdown
                                    clickToHide
                                    position={"bottomRight"}
                                    render={
                                      <Dropdown.Menu>
                                        <Dropdown.Item
                                          icon={<DeleteIcon />}
                                          disabled={arrayFields.length <= 1}
                                          onClick={() => onDeleteRow(i, key)}
                                        >
                                          Delete
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          disabled={
                                            replaceWhitespace(
                                              sequences[i].entity_sequence
                                            ).length <= 0
                                          }
                                          icon={
                                            sequences[i].isCollapse ? (
                                              <MaximizeIcon />
                                            ) : (
                                              <MinimizeIcon />
                                            )
                                          }
                                          onClick={() => onCollapseToggle(i)}
                                        >
                                          {sequences[i].isCollapse
                                            ? "Expand"
                                            : "Collapse"}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                          icon={<AtIcon />}
                                          onClick={() => onEditHotspot(i)}
                                        >
                                          Specify hotspots
                                        </Dropdown.Item>
                                      </Dropdown.Menu>
                                    }
                                  >
                                    <IconMore className={styles.iconMore} />
                                  </Dropdown>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="sequence-footer">
                      <div className="left">
                        <Button
                          theme="light"
                          type="tertiary"
                          size="small"
                          disabled={arrayFields.length >= 6}
                          className="border-btn"
                          icon={<IconPlus />}
                          onPointerDown={() => {
                            if (arrayFields.length >= 6) {
                              return;
                            }
                            addWithInitValue({
                              entity_type: BinderDesignTypeEnum.ProteinChain,
                              copies: 1,
                              sequence: "",
                              isEdit: true,
                            });
                          }}
                        >
                          Add Entity
                        </Button>

                        <Tooltip
                          content="Think deeper for better design quality"
                          style={{ maxWidth: "unset" }}
                        >
                          <Button
                            theme="borderless"
                            type="tertiary"
                            size="small"
                            onClick={onToggleDeepThink}
                            className={classNames({
                              "border-btn": true,
                              active: isDeepThink,
                            })}
                            icon={<BulbIcon />}
                            style={{ marginLeft: "16px" }}
                          >
                            DeepThink
                          </Button>
                        </Tooltip>
                      </div>
                      <div className="right">
                        <Tooltip content="Use simple mode">
                          <Button
                            theme="borderless"
                            size="small"
                            icon={<ArrowLeftIcon />}
                            style={{
                              width: "28px",
                              color: "var(--semi-color-text-2)",
                            }}
                            onClick={onModeChange}
                          ></Button>
                        </Tooltip>

                        <Button
                          theme="solid"
                          type="primary"
                          size="small"
                          className={styles.searchBtn}
                          style={{ marginLeft: "8px" }}
                          loading={loading}
                          onClick={onSubmit}
                          icon={<IconSearch />}
                        ></Button>
                        <span
                          style={{
                            display: "inline-block",
                            width: "16px",
                          }}
                        ></span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </ArrayField>
          );
        }}
      </Form>

      {loginVisible && (
        <JobLoginDialog
          cancel={onLoginCancel}
          submit={onLoginSubmit}
          logined={onLogined}
          loading={loadingInfo}
          type={userType}
          tokenInfo={tokenInfo}
          username={username}
          submitingJob={submitingJob}
          // type="anonymous"
        />
      )}

      {provideDialogVisible && editedRow && (
        <ProvideStructureDialog
          row={editedRow}
          cancel={onCancelProvide}
          submit={(data: ProvideStructureDataModel, fid: string) =>
            onProvideSubmit(data, fid)
          }
          reset={onProvideReset}
        />
      )}
    </>
  );
};

export default withField(ProteinBinderDesignTableField);
